import React, { useEffect, useRef, useState } from 'react';
import mermaid from 'mermaid';
import { Box, Typography } from '@mui/material';

// Initialize mermaid
mermaid.initialize({
    startOnLoad: false,
    theme: 'default',
    securityLevel: 'loose',
    fontFamily: 'Quicksand, sans-serif'
});

interface MermaidDiagramProps {
    chart: string;
}

const MermaidDiagram: React.FC<MermaidDiagramProps> = ({ chart }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [svg, setSvg] = useState<string>('');
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const renderChart = async () => {
            if (!chart || !containerRef.current) return;

            try {
                // Unique ID for each render to avoid conflicts
                const id = `mermaid-${Math.random().toString(36).substr(2, 9)}`;

                // Clear previous error
                setError(null);

                // Attempt to parse first to catch syntax errors
                try {
                    await mermaid.parse(chart);
                } catch (e: any) {
                    console.error("Mermaid parse error:", e);
                    setError("Erreur de syntaxe Mermaid: " + e.message);
                    return;
                }

                // Render
                const { svg } = await mermaid.render(id, chart);
                setSvg(svg);
            } catch (err: any) {
                console.error("Mermaid render error:", err);
                setError("Erreur de rendu du diagramme.");
            }
        };

        renderChart();
    }, [chart]);

    if (error) {
        return (
            <Box sx={{ p: 2, border: '1px solid #ffcccb', borderRadius: 1, bgcolor: '#fff5f5', color: '#c62828' }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{error}</Typography>
                <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>Code source:</Typography>
                <pre style={{ margin: 0, fontSize: '0.8rem', overflow: 'auto' }}>{chart}</pre>
            </Box>
        );
    }

    return (
        <Box
            ref={containerRef}
            sx={{
                display: 'flex',
                justifyContent: 'center',
                my: 2,
                overflowX: 'auto',
                '& svg': { maxWidth: '100%', height: 'auto' }
            }}
            dangerouslySetInnerHTML={{ __html: svg }}
        />
    );
};

export default MermaidDiagram;
